import React from "react";

import { List as ListIcon } from "@mui/icons-material";
import { Typography, Box, Grid, Alert, CircularProgress } from "@mui/material";

import { EmployeePosition } from "../../api/PartnerPortalGatewayApi";
import { CACard } from "../ui";
import { EducatorCard } from "./EducatorCard/EducatorCard";

interface RecentlyWorkedEducatorsGridProps {
  data: EmployeePosition[] | undefined;
  isLoading: boolean;
  error?: Error | null;
}

export const RecentlyWorkedEducatorsGrid: React.FC<RecentlyWorkedEducatorsGridProps> = ({
  data,
  isLoading,
  error,
}) => {

  if (isLoading) {
    return (
      <CACard>
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading educators...
          </Typography>
        </Box>
      </CACard>
    );
  }

  if (error) {
    return (
      <CACard>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      </CACard>
    );
  }

  if (!data || data.length === 0) {
    return (
      <CACard>
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
          }}
        >
          <ListIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
            No educators have recently worked at your service
          </Typography>
        </Box>
      </CACard>
    );
  }

  return (
    <CACard>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            <ListIcon sx={{ fontSize: "1.5rem", color: "secondary.main" }} />
            Educators who have recently worked here
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
            The following have worked at your service in the last 3 weeks
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {data.map((educator) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={educator.employeeId}>
              <EducatorCard educator={educator} />
            </Grid>
          ))}
        </Grid>
      </Box>
    </CACard>
  );
};
